/**
 * Utility for flattening JSON objects into key-value pairs suitable for form submission
 * Handles nested objects, arrays, and various data types according to Rails conventions
 */
export class FormDataFlattener {
  /**
   * Flattens a JSON object into an array of key-value pairs
   * @param {Object} obj - The object to flatten
   * @param {string} prefix - The prefix for nested keys (used internally)
   * @returns {Array<{key: string, value: string}>} Array of key-value pairs
   */
  static flatten(obj, prefix = '', useIndexForArrays = false) {
    const result = [];

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        const newKey = prefix ? `${prefix}[${key}]` : key;

        // Skip undefined values
        if (value === undefined) {
          continue;
        }

        // Handle null values explicitly
        if (value === null) {
          result.push({ key: newKey, value: '' });
          continue;
        }

        // Handle Date objects
        if (value instanceof Date) {
          result.push({ key: newKey, value: value.toISOString() });
          continue;
        }

        // Handle nested objects (but not arrays)
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          // Skip functions and other non-serializable objects
          if (typeof value === 'function' || value instanceof RegExp) {
            continue;
          }
          result.push(...this.flatten(value, newKey));
        } else if (Array.isArray(value)) {
          value.forEach((item, index) => {
            const arrayItemKey = this.arrayItemKey(newKey, index, useIndexForArrays);
            if (item === undefined) {
              // Skip undefined array items
              return;
            }
            if (item === null) {
              result.push({ key: arrayItemKey, value: '' });
            } else if (typeof item === 'object' && item !== null) {
              // Skip functions and other non-serializable objects in arrays
              if (typeof item === 'function' || item instanceof RegExp) {
                return;
              }
              if (item instanceof Date) {
                result.push({ key: arrayItemKey, value: item.toISOString() });
              } else {
                result.push(...this.flatten(item, arrayItemKey));
              }
            } else {
              result.push({ key: arrayItemKey, value: String(item) });
            }
          });
        } else {
          // Handle primitive values
          result.push({ key: newKey, value: String(value) });
        }
      }
    }

    return result;
  }

  static arrayItemKey(key, index, useIndexForArrays = false) {
    if (useIndexForArrays) {
      return `${key}[${index}]`;
    } else {
      return `${key}[]`;
    }
  }

  /**
   * Creates a FormData object from a flattened JSON object
   * @param {Object} obj - The object to convert to FormData
   * @returns {FormData} FormData object ready for submission
   */
  static toFormData(obj) {
    const formData = new FormData();
    const flattened = this.flatten(obj);

    flattened.forEach(({ key, value }) => {
      formData.append(key, value);
    });

    return formData;
  }

  /**
   * Creates a URLSearchParams object from a flattened JSON object
   * @param {Object} obj - The object to convert to URLSearchParams
   * @returns {URLSearchParams} URLSearchParams object ready for query strings
   */
  static toURLSearchParams(obj) {
    const params = new URLSearchParams();
    const flattened = this.flatten(obj);

    flattened.forEach(({ key, value }) => {
      params.append(key, value);
    });

    return params;
  }
}
